import {
  BracketRoundHeader,
  BracketRow,
  CenterLineColumn,
  GapColumn,
  NodeColumn,
  StretchLineColumn,
} from "./styles";
import {
  BracketGraph,
  bracketGraphHasGrandFinal,
  getChildrenNodesByDepth,
  getMaxLowerDepth,
  getMaxUpperDepth,
} from "model/Bracket/BracketGraph";
import { getPaddedRow, mapRowWithBracketLines } from "./BracketRow";
import {
  createBracketLineGrandFinalInto,
  createBracketLineGrandFinalLower,
  createBracketLineGrandFinalUpper,
  createBracketLineGrandFinalVertical,
} from "./Lines/BracketLines";
import { BracketRowMatch } from "./BracketRowMatch";
import { BracketMatchArgs } from "./BracketMatchArgs";
import { depthToFullTitleDoubleElim } from "model/Bracket/BracketTitles";
import { JSX } from "react";

interface Props {
  bracketGraph: BracketGraph;
  compact: boolean;
  tournamentId: string;
  bracketMatchArgs?: BracketMatchArgs;
}

export const DoubleElimBracket = ({
  bracketGraph,
  compact,
  tournamentId,
  bracketMatchArgs,
}: Props) => {
  const hasGrandFinal = bracketGraphHasGrandFinal(bracketGraph);
  const maxUpperDepth = getMaxUpperDepth(bracketGraph);
  const maxLowerDepth = getMaxLowerDepth(bracketGraph);
  const { upperNodesByDepth, lowerNodesByDepth } = getChildrenNodesByDepth(
    bracketGraph.finalNodes
  );

  const upperSpaceColumns: number[] = [];
  for (var i = 1; i < lowerNodesByDepth.length - 1; i++) {
    if (
      tournamentId !== "Smsx1" &&
      lowerNodesByDepth[i].length !== lowerNodesByDepth[i + 1].length
    ) {
      if (i < upperNodesByDepth.length) {
        upperSpaceColumns.push(i - upperSpaceColumns.length);
      }
    }
  }

  let upperPadding =
    lowerNodesByDepth.length -
    (upperNodesByDepth.length + upperSpaceColumns.length);
  upperPadding = upperPadding > 0 ? upperPadding : 0;
  let { titleCells: upperTitleCells, nodeCells: upperNodeCells } =
    mapRowWithBracketLines(
      bracketMatchArgs,
      tournamentId,
      upperNodesByDepth,
      upperSpaceColumns,
      maxUpperDepth,
      false,
      true,
      compact,
      upperPadding
    );

  let lowerPadding = upperNodesByDepth.length - lowerNodesByDepth.length;
  lowerPadding = lowerPadding > 0 ? lowerPadding : 0;
  let { titleCells: lowerTitleCells, nodeCells: lowerNodeCells } =
    mapRowWithBracketLines(
      bracketMatchArgs,
      tournamentId,
      lowerNodesByDepth,
      [],
      maxLowerDepth,
      true,
      true,
      compact,
      lowerPadding
    );

  if (tournamentId === "Wrlx2") {
    lowerTitleCells.unshift(
      <>
        <CenterLineColumn compact={compact} />
        <NodeColumn />
      </>
    );

    lowerNodeCells.unshift(
      <>
        <CenterLineColumn compact={compact} />
        <NodeColumn />
      </>
    );
  }

  let middleTitleCells: JSX.Element[] | undefined;
  let middleNodeCells: JSX.Element[] | undefined;
  if (hasGrandFinal) {
    const finalNode = bracketGraph.finalNodes[0];
    const middleRow = getPaddedRow(
      Math.max(lowerNodeCells.length, upperNodeCells.length) +
        (tournamentId === "Wrlx2" ? 1 : 0),
      compact
    );
    middleTitleCells = middleRow.titleCells;
    middleNodeCells = middleRow.nodeCells;

    middleTitleCells.push(
      <StretchLineColumn compact={compact}>
        {createBracketLineGrandFinalVertical()}
      </StretchLineColumn>
    );

    middleTitleCells.push(
      <NodeColumn>
        <BracketRoundHeader isLower={true}>
          {depthToFullTitleDoubleElim(0, 1, false, false)}
        </BracketRoundHeader>
      </NodeColumn>
    );

    middleNodeCells.push(
      <StretchLineColumn compact={compact}>
        {createBracketLineGrandFinalInto()}
      </StretchLineColumn>
    );

    middleNodeCells.push(
      <NodeColumn>
        <BracketRowMatch
          node={finalNode}
          tournamentId={tournamentId}
          args={bracketMatchArgs}
        />
      </NodeColumn>
    );

    upperNodeCells.push(
      <StretchLineColumn compact={compact}>
        {createBracketLineGrandFinalUpper()}
      </StretchLineColumn>
    );

    lowerNodeCells.push(
      <StretchLineColumn compact={compact}>
        {createBracketLineGrandFinalLower()}
      </StretchLineColumn>
    );

    lowerTitleCells.push(
      <StretchLineColumn compact={compact}>
        {createBracketLineGrandFinalVertical()}
      </StretchLineColumn>
    );
  }

  return (
    <div style={{ padding: "10px", marginTop: "10px" }}>
      <BracketRow>{upperTitleCells}</BracketRow>
      <BracketRow>{upperNodeCells}</BracketRow>
      {!!middleTitleCells && <BracketRow>{middleTitleCells}</BracketRow>}
      {!!middleNodeCells && <BracketRow>{middleNodeCells}</BracketRow>}
      <BracketRow>{lowerTitleCells}</BracketRow>
      <BracketRow>{lowerNodeCells}</BracketRow>
    </div>
  );
};
